import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { classMarksAPI, serviceAPI, isAuthenticated } from '../services/api';
import './CreateClassMarks.css';

const CreateClassMarks: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [requestStatus, setRequestStatus] = useState<'approved' | 'pending' | 'no_request' | null>(null);
  const [approvedRequest, setApprovedRequest] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    school_name: '',
    school_registration_number: '',
    class_name: '',
    term: '',
    exam_type: '',
    exam_year: new Date().getFullYear(),
    exam_month: '',
    center_number: '',
    center_name: '',
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  // SVG Icons
  const SpinnerIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#e8ecf1" strokeWidth="4"/>
      <path d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22" stroke="#2c3e50" strokeWidth="4" strokeLinecap="round"/>
    </svg>
  );

  const ClockIcon = () => (
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#f57c00" strokeWidth="2"/>
      <path d="M12 6V12L16 14" stroke="#f57c00" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const LockIcon = () => (
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="11" width="18" height="11" rx="2" stroke="#2c3e50" strokeWidth="2"/>
      <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="#2c3e50" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const ErrorIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#dc3545" strokeWidth="2"/>
      <path d="M12 8V12M12 16H12.01" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const ServiceIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4H20V20H4V4Z" stroke="#2c3e50" strokeWidth="1.5"/>
      <path d="M8 8H16M8 12H14M8 16H12" stroke="#2c3e50" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M18 4L14 8" stroke="#2c3e50" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );

  const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  // Helper function to get status display
  const getStatusDisplay = (status: string) => {
    const statusMap: Record<string, string> = {
      'pending': 'Pending Approval',
      'in_review': 'In Review',
      'approved': 'Approved',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'cancelled': 'Cancelled',
    };
    return statusMap[status] || status;
  };

  // Helper function to get status class
  const getStatusClass = (status: string) => {
    const classMap: Record<string, string> = {
      'pending': 'ccm-status-pending',
      'in_review': 'ccm-status-review',
      'approved': 'ccm-status-approved',
      'in_progress': 'ccm-status-progress',
      'completed': 'ccm-status-completed',
      'cancelled': 'ccm-status-cancelled',
    };
    return classMap[status] || '';
  };

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    checkUserAccess();
  }, []);

  const checkUserAccess = async () => {
    setCheckingAccess(true);
    try {
      // Use the serviceAPI to get user's service requests
      const response = await serviceAPI.getMyServiceRequests();
      const requests = response.data || [];
      
      console.log('All Service Requests:', requests);
      
      // Find all class marks related requests
      const classMarksRequests = requests.filter(
        (req: any) => {
          const serviceName = (req.service_name_display || req.service_name || '').toLowerCase();
          return serviceName.includes('class marks') || 
                 serviceName.includes('marks record') ||
                 serviceName.includes('record management');
        }
      );
      
      console.log('Class Marks Requests:', classMarksRequests);
      
      if (classMarksRequests.length > 0) {
        // Check if any request is approved
        const approved = classMarksRequests.find((req: any) => req.status === 'approved');
        
        if (approved) {
          setHasAccess(true);
          setRequestStatus('approved');
          setApprovedRequest(approved);
          setPendingRequests([]);
        } else {
          // Get all pending/in_review requests
          const pending = classMarksRequests.filter(
            (req: any) => req.status === 'pending' || req.status === 'in_review'
          );
          
          if (pending.length > 0) {
            setPendingRequests(pending);
            setRequestStatus('pending');
            setHasAccess(false);
            setApprovedRequest(null);
          } else {
            // All requests are cancelled/rejected
            setPendingRequests([]);
            setRequestStatus('no_request');
            setHasAccess(false);
            setApprovedRequest(null);
          }
        }
      } else {
        // No class marks request found
        setRequestStatus('no_request');
        setHasAccess(false);
        setPendingRequests([]);
        setApprovedRequest(null);
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setError('Failed to verify access permissions');
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Use the classMarksAPI to create the record
      const response = await classMarksAPI.createRecord(formData);
      
      if (response.data.record_id) {
        navigate(`/dashboard/class-marks/${response.data.record_id}`);
      }
    } catch (err: any) {
      console.error('Error creating record:', err);
      setError(err.response?.data?.error || 'Failed to create marks record');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAccess) {
    return (
      <div className="ccm-page">
        <div className="ccm-container">
          <div className="ccm-access-check">
            <div className="ccm-spinner-wrapper">
              <SpinnerIcon />
            </div>
            <p>Verifying access permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show pending message if there are pending requests
  if (requestStatus === 'pending' && pendingRequests.length > 0) {
    return (
      <div className="ccm-page">
        <div className="ccm-container">
          <div className="ccm-access-denied">
            <div className="ccm-access-icon">
              <ClockIcon />
            </div>
            <h2>Requests Pending Approval</h2>
            <p>
              You have {pendingRequests.length} service request{pendingRequests.length > 1 ? 's' : ''} pending for 
              <strong> Class Marks Record Management</strong>.
            </p>
            <div className="ccm-pending-requests">
              {pendingRequests.map((request, index) => (
                <div key={request.id} className="ccm-pending-request-item">
                  <div className="ccm-request-header">
                    <span className="ccm-request-number">Request #{index + 1}</span>
                    <span className={`ccm-status-badge ${getStatusClass(request.status)}`}>
                      {getStatusDisplay(request.status)}
                    </span>
                  </div>
                  <div className="ccm-request-details">
                    <p><strong>Service:</strong> {request.service_name_display || request.service_name || 'Class Marks Record Management'}</p>
                    <p><strong>Submitted:</strong> {new Date(request.created_at).toLocaleDateString()}</p>
                    <p><strong>Request ID:</strong> #{request.id}</p>
                    {request.updated_at && (
                      <p><strong>Last Updated:</strong> {new Date(request.updated_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              ))}
              <div className="ccm-pending-message">
                <p>
                  Your requests are being reviewed by our administrators. You will be notified once 
                  any of your requests are approved. You can then access this feature.
                </p>
                <p className="ccm-pending-note">
                  <strong>Note:</strong> Approval typically takes 1-2 business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show no request message
  if (requestStatus === 'no_request' || (!hasAccess && pendingRequests.length === 0)) {
    return (
      <div className="ccm-page">
        <div className="ccm-container">
          <div className="ccm-access-denied">
            <div className="ccm-access-icon">
              <LockIcon />
            </div>
            <h2>Access Required</h2>
            <p>
              You need an approved service request for <strong>Class Marks Record Management</strong> 
              to create marks records.
            </p>
            <div className="ccm-request-actions">
              <div className="ccm-service-info">
                <div className="ccm-service-icon-wrapper">
                  <ServiceIcon />
                </div>
                <h4>Class Marks Record Management</h4>
                <p>
                  Efficiently manage student examination results, calculate grades, divisions, 
                  and generate comprehensive performance reports.
                </p>
              </div>
              <button 
                className="ccm-btn ccm-btn-primary"
                onClick={() => navigate('/dashboard/services')}
              >
                Submit Service Request
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show the form if access is approved
  return (
    <div className="ccm-page">
      <div className="ccm-container">
        <div className="ccm-header">
          <h1 className="ccm-title">Create Marks Record</h1>
          <p className="ccm-subtitle">Set up a new class marks record for examination results</p>
          {approvedRequest && (
            <div className="ccm-approved-badge">
              <CheckIcon />
              <span>Approved Request #{approvedRequest.id}</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="ccm-form">
          {error && (
            <div className="ccm-error">
              <span className="ccm-error-icon">
                <ErrorIcon />
              </span>
              <span>{error}</span>
            </div>
          )}

          <div className="ccm-section">
            <h3 className="ccm-section-title">School Information</h3>
            <div className="ccm-row">
              <div className="ccm-group">
                <label className="ccm-label">School Name *</label>
                <input
                  type="text"
                  name="school_name"
                  className="ccm-input"
                  placeholder="Enter school name"
                  value={formData.school_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="ccm-group">
                <label className="ccm-label">School Registration Number</label>
                <input
                  type="text"
                  name="school_registration_number"
                  className="ccm-input"
                  placeholder="e.g., S.1234/2023"
                  value={formData.school_registration_number}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="ccm-section">
            <h3 className="ccm-section-title">Class & Exam Information</h3>
            <div className="ccm-row">
              <div className="ccm-group">
                <label className="ccm-label">Class *</label>
                <select
                  name="class_name"
                  className="ccm-input"
                  value={formData.class_name}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Class</option>
                  <option value="form_one">Form One</option>
                  <option value="form_two">Form Two</option>
                  <option value="form_three">Form Three</option>
                  <option value="form_four">Form Four</option>
                  <option value="form_five">Form Five</option>
                  <option value="form_six">Form Six</option>
                </select>
              </div>
              <div className="ccm-group">
                <label className="ccm-label">Term *</label>
                <select
                  name="term"
                  className="ccm-input"
                  value={formData.term}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Term</option>
                  <option value="term_one">Term One</option>
                  <option value="term_two">Term Two</option>
                  <option value="term_three">Term Three</option>
                </select>
              </div>
            </div>

            <div className="ccm-row">
              <div className="ccm-group">
                <label className="ccm-label">Exam Type *</label>
                <select
                  name="exam_type"
                  className="ccm-input"
                  value={formData.exam_type}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Exam Type</option>
                  <option value="terminal">Terminal Examination</option>
                  <option value="mid_term">Mid-Term Examination</option>
                  <option value="annual">Annual Examination</option>
                  <option value="mock">Mock Examination</option>
                </select>
              </div>
              <div className="ccm-group">
                <label className="ccm-label">Exam Year *</label>
                <select
                  name="exam_year"
                  className="ccm-input"
                  value={formData.exam_year}
                  onChange={handleChange}
                  required
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ccm-row">
              <div className="ccm-group">
                <label className="ccm-label">Exam Month</label>
                <select
                  name="exam_month"
                  className="ccm-input"
                  value={formData.exam_month}
                  onChange={handleChange}
                >
                  <option value="">Select Month</option>
                  <option value="January">January</option>
                  <option value="February">February</option>
                  <option value="March">March</option>
                  <option value="April">April</option>
                  <option value="May">May</option>
                  <option value="June">June</option>
                  <option value="July">July</option>
                  <option value="August">August</option>
                  <option value="September">September</option>
                  <option value="October">October</option>
                  <option value="November">November</option>
                  <option value="December">December</option>
                </select>
              </div>
            </div>
          </div>

          <div className="ccm-section">
            <h3 className="ccm-section-title">Examination Center Information</h3>
            <div className="ccm-row">
              <div className="ccm-group">
                <label className="ccm-label">Center Number</label>
                <input
                  type="text"
                  name="center_number"
                  className="ccm-input"
                  placeholder="e.g., S1234"
                  value={formData.center_number}
                  onChange={handleChange}
                />
              </div>
              <div className="ccm-group">
                <label className="ccm-label">Center Name</label>
                <input
                  type="text"
                  name="center_name"
                  className="ccm-input"
                  placeholder="Enter center name"
                  value={formData.center_name}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="ccm-actions">
            <button
              type="button"
              className="ccm-btn ccm-btn-cancel"
              onClick={() => navigate('/dashboard/class-marks')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ccm-btn ccm-btn-submit"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateClassMarks;